/* eslint-disable react/default-props-match-prop-types */
import * as React from 'react';
import PropTypes from 'prop-types';
import underscore from 'underscore';
import * as microsoftPaymentJS from '@cspayments/microsoft-payments-js';
import { getScaChallengeStyles } from '@bingads-webui-campaign-react/common-styles';
import { Modal } from '@bingads-webui-react/primitive';
import { withDefaultStyles, type PropsWithDefaultStyle, type TGetStyles } from '@bingads-webui-react/with-default-styles';
import { awa } from '@bingads-webui/jsll';
import { getChallengeAdditionalHeaders } from './additional-headers';
import type {
  PaasChallengerProps,
  PaasChallengerState,
  PaymentChallengeIdentifiers,
} from './types';

export type {
  PaasChallengeEventHandlers,
  PaasChallengerProps,
  PaasChallengerState,
  PaymentAsAServiceApi,
  PaymentApiPrimitiveId,
  PaymentChallengeIdentifiers,
  PaymentChallengeResponse,
  PidlLogger,
  WalletRequestValidationResponse,
} from './types';

type PaasChallengerBaseProps = Omit<PropsWithDefaultStyle<PaasChallengerProps>, 'classes'> & {
  classes: NonNullable<PaasChallengerProps['classes']>;
};

const ModalTitle = (Modal as typeof Modal & {
  Title: React.ComponentType<React.PropsWithChildren<object>>;
}).Title;

const getStyles: TGetStyles = (_, { palette = {} } = {}) => ({
  '@global': getScaChallengeStyles({ palette, variant: 'paas' }).global['@global'],
  paasChallenger: {
    '&.hidden': {
      display: 'none',
    },
    '& .modal-dialog': {
      minWidth: '550px',
      maxWidth: 'unset',
      '& .modal-body': {
        paddingBottom: 0,
      },
    },
    '& #paas-challenger-pidl-form-container': {
      /* BEGIN: CVV challenger style. CVV challenge view should show for Mexico cards. */
      display: 'flex',
      flexDirection: 'column',
      '& .pidlddc-div': { // general style overwrite
        marginBottom: 0,
      },
      '& #pidlddc-text-challengecvvText': { // header - title
        marginTop: 0,
        marginBottom: '20px',
      },
      '& #pidlddc-div-cvvToken': { // middle - the cvv input
        display: 'flex',
        '& #pidlInput_cvvToken': {
          marginLeft: '15px',
          minWidth: '120px',
        },
      },
      '& #pidlddc-div-pagesubmitblock': { // bottom - action buttons
        marginTop: '20px',
        marginBottom: '20px',
        display: 'flex',
        '& #pidlddc-button-cancelButton': {
          marginLeft: '10px',
          order: 2,
        },
      },
      /* END: CVV challenger style */
      /* BEGIN: PSD2 challenger style. PSD2 challenge view should show for Euro cards. */
      '& iframe:not(#pidlddc-secure-input-cvvToken)': {
        minHeight: '600px',
      },
      /* END: PSD2 challenger style */
    },
  },
});

class PaasChallengerBase extends React.Component<PaasChallengerBaseProps, PaasChallengerState> {
  static propTypes = {
    paymentAsAServiceApi: PropTypes.shape({
      walletRequestValidate: PropTypes.func.isRequired,
      createPaymentReq: PropTypes.func.isRequired,
    }).isRequired,
    aid: PropTypes.number.isRequired,
    cid: PropTypes.number.isRequired,
    walletRequestId: PropTypes.string,
    mtPiId: PropTypes.number,
    chargeAmount: PropTypes.number,
    isRemoveHold: PropTypes.bool,
    isPidlSecureFieldsIsolationAdoptionEnabled: PropTypes.bool,
    eventHandlers: PropTypes.shape({
      handleCancel: PropTypes.func,
      handleScaChallengeSuccess: PropTypes.func,
      handleScaChallengeFailure: PropTypes.func,
      handleDataLoading: PropTypes.func,
      handleDataLoaded: PropTypes.func,
    }),
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    pidlEnvironment: PropTypes.string.isRequired,
    pidlLogger: PropTypes.shape({
      setOptions: PropTypes.func.isRequired,
      succeed: PropTypes.func.isRequired,
      fail: PropTypes.func.isRequired,
      userFail: PropTypes.func.isRequired,
    }).isRequired,
    classes: PropTypes.shape({
      paasChallenger: PropTypes.string,
    }).isRequired,
  } as React.WeakValidationMap<PaasChallengerBaseProps>;

  static defaultProps: Partial<PaasChallengerBaseProps> = {
    walletRequestId: null,
    mtPiId: null,
    chargeAmount: null,
    isRemoveHold: false,
    isPidlSecureFieldsIsolationAdoptionEnabled: false,
    eventHandlers: {
      handleDataLoading: () => {},
      handleDataLoaded: () => {},
      handleCancel: () => {},
      handleScaChallengeSuccess: () => {},
      handleScaChallengeFailure: () => {},
    },
  };

  constructor(props: PaasChallengerBaseProps) {
    super(props);

    if (!props.pidlLogger) {
      throw new Error('pidlLogger is required but not provided');
    }

    this.state = {
      showModal: false,
    };

    this.paymentClient = new microsoftPaymentJS.PaymentClient();
  }

  componentDidMount() {
    this.initializePaymentChallenge();
  }

  paymentClient: microsoftPaymentJS.PaymentClient;

  createPaymentRequest = async () => {
    const {
      paymentAsAServiceApi, aid, cid, walletRequestId, isRemoveHold,
    } = this.props;

    // Add PI scenario
    if (walletRequestId) {
      const {
        ClientAccessToken,
        PaymentInstrumentId,
        PaymentRequestId,
        ValidationStatus,
      } = await paymentAsAServiceApi.walletRequestValidate({ CustomerId: cid, WalletRequestId: walletRequestId });

      return {
        Status: ValidationStatus, ClientAccessToken, PaymentInstrumentId, PaymentRequestId,
      };
    }

    // Add funds / Bill me now scenario
    const { chargeAmount, mtPiId } = this.props;

    if (!underscore.isNumber(chargeAmount)) {
      throw new Error(`Expect chargeAmount to be a number, but got ${chargeAmount}`);
    }
    if (!underscore.isNumber(mtPiId)) {
      throw new Error(`Expect mtPiId to be a number, but got ${mtPiId}`);
    }

    const { Status, ClientAccessToken, PaymentRequestId } = await paymentAsAServiceApi.createPaymentReq({
      AccountId: aid,
      ChargeAmount: chargeAmount,
      CustomerId: cid,
      IsSubscription: !!isRemoveHold,
      PaymentInstrumentId: mtPiId,
    });

    return { Status, ClientAccessToken, PaymentRequestId };
  };

  initializePaymentChallenge = async () => {
    const {
      pidlLogger,
      aid,
      cid,
      walletRequestId,
      mtPiId,
      chargeAmount,
      isRemoveHold,
    } = this.props;
    const pidlEnvironment = this.props.pidlEnvironment.toLowerCase();

    pidlLogger.setOptions({
      paymentAsAService: 'challenge',
      componentProps: {
        aid,
        cid,
        walletRequestId,
        mtPiId,
        chargeAmount,
        isRemoveHold,
        pidlEnvironment,
      },
    });

    this.props.eventHandlers.handleDataLoading();

    try {
      const {
        Status,
        ClientAccessToken,
        PaymentInstrumentId, // optional, a GUID representing the PAAS PI ID, only has value for Add PI scenario
        PaymentRequestId,
      } = await this.createPaymentRequest();

      pidlLogger.setOptions({
        mtResponse: {
          Status,
          PaymentInstrumentId,
          PaymentRequestId,
        },
      });

      if (Status !== 'PendingClientAction') {
        this.handleSuccess({ PaymentRequestId, PaymentInstrumentId });
        return;
      }

      this.setState({
        showModal: true,
        visible: false,
      });

      const additionalHeaders = getChallengeAdditionalHeaders(
        pidlEnvironment,
        this.props.isPidlSecureFieldsIsolationAdoptionEnabled
      );

      const correlationVector = !awa.cv.isInit() ? awa.cv.init() : awa.cv.increment();
      const telemetryOptions = {
        contextData: {
          cvInitValue: correlationVector,
        },
      };
      pidlLogger.setOptions({
        correlationVector,
      });

      await this.paymentClient.initialize({
        clientAccessToken: ClientAccessToken,
        additionalHeaders,
        uxContext: {
          showPlaceholderInTextbox: true,
          secureFieldPreLoadingEnabled: true,
          showSubmitBlock: true,
        },
        challengeOptions: {
          windowSize: 3, // 500 x 600
        },
        onSuccess: (pidlResponse: unknown) => this.handleSuccess({ PaymentRequestId, PaymentInstrumentId }, pidlResponse),
        onFailure: this.handleFailure,
        onCancel: this.handleCancel,
        env: pidlEnvironment,
        telemetryOptions,
      });

      await this.paymentClient.renderConfirm('paas-challenger-pidl-form-container');

      this.setState({ visible: true });
    } catch (error) {
      this.handleInitError(error);
    } finally {
      this.props.eventHandlers.handleDataLoaded();
    }
  };

  handleInitError = (error: unknown) => {
    // Route request-setup failures through the same path as PIDL challenge failures.
    // This resets the parent flow (e.g. signup "submitting" / "challenge" state) so the
    // user can retry from the form with a fresh request, and surfaces the caller's
    // contextual error message. We intentionally do NOT retry in place, because the
    // underlying calls (createPaymentReq / walletRequestValidate) are not guaranteed to be
    // idempotent for the same walletRequestId / charge.
    this.handleFailure(error);
  };

  handleSuccess = ({
    PaymentRequestId,
    PaymentInstrumentId,
  }: PaymentChallengeIdentifiers, pidlResponse?: unknown) => {
    this.setState({ showModal: false });

    // The new pidlLogger.succeed() does not accept an additionalDetails object as input. Refer: pidl-payment-method-actions-base/src/utils/logger.js
    // To be compactible with both old and new logger, use setOptions instead.
    this.props.pidlLogger.setOptions({
      pidlResponse,
    });
    this.props.pidlLogger.succeed();
    this.props.eventHandlers.handleScaChallengeSuccess(PaymentRequestId, PaymentInstrumentId);
  };

  handleFailure = (error: unknown) => {
    this.setState({ showModal: false });
    this.props.pidlLogger.fail(error);
    this.props.eventHandlers.handleScaChallengeFailure(error);
  };

  handleCancel = () => {
    this.setState({ showModal: false });
    this.props.pidlLogger.userFail('CancelClicked');
    this.props.eventHandlers.handleCancel();
  };

  render() {
    const { showModal, visible } = this.state;
    const { i18n, classes } = this.props;

    return (
      <Modal show={showModal} backdrop="static" keyboard={false} className={`paas-challenger-dialog ${classes.paasChallenger} ${visible ? '' : 'hidden'}`}>
        <Modal.Header closeButton onHide={this.handleCancel}>
          <ModalTitle>{i18n.getString(_TL_('Authenticate your card'))}</ModalTitle>
        </Modal.Header>
        <Modal.Body>
          <div id="paas-challenger-pidl-form-container" />
        </Modal.Body>
      </Modal>
    );
  }
}

export const PaasChallenger = withDefaultStyles(PaasChallengerBase, getStyles);
